import { useState, useEffect } from 'react';
import { useAuthToken } from "./TokenContext";
import { 
  getCredentials, 
  getCredentialStatus, 
  refreshCredentials,
  subscribeToCredentials,
  initializeCredentials
} from './credentialsService';

/**
 * React hook to use credentials in components
 * @returns {Object} Credential data and status
 */
export function useCredentials() {
  const token = useAuthToken(); // Get token directly in the hook
  const [credentials, setCredentials] = useState(getCredentials());
  const [status, setStatus] = useState(getCredentialStatus());
  const [refreshing, setRefreshing] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  // Initialize credentials when the token is available
  useEffect(() => {
    if (token && !initialized) {
      initializeCredentials(token).then(success => {
        setInitialized(success);
      });
    }
  }, [token, initialized]);
  
  // Subscribe to credential changes
  useEffect(() => {
    const unsubscribe = subscribeToCredentials((newCredentials) => {
      setCredentials(newCredentials);
      setStatus(getCredentialStatus());
      setRefreshing(false);
    });
    
    return unsubscribe;
  }, []);
  
  // Refresh credentials
  const refresh = async () => {
    if (!token) return false;
    
    setRefreshing(true);
    const success = await refreshCredentials(token);
    if (!success) {
      setRefreshing(false);
    }
    return success;
  };
  
  return {
    credentials,
    status,
    isLoaded: status === 'loaded',
    isLoading: status === 'loading',
    hasError: status === 'error',
    refreshing,
    refresh,
    initialized
  };
}

export default useCredentials;